import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import article from "./InvestoContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";
import './PagesCss/StockProfitCalculator.css';

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
);

const RetirementInvestmentCalculator = () => {
  const content = article.find((item) => item.id === 3);
  const [mode, setMode] = useState("accumulation");
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [riskLevel, setRiskLevel] = useState("moderate");
  const [notifications, setNotifications] = useState([]);

  const [inputs, setInputs] = useState({
    currentAge: "35",
    retirementAge: "65",
    lifeExpectancy: "85",
    currentSavings: "50000.00",
    contribution: "1000.00",
    contributionFrequency: "monthly",
    annualReturn: "8.00",
    targetCorpus: "1000000.00",
    inflationRate: "3.00",
    annualExpenses: "50000.00",
    taxRate: "15.00",
    stepUp: "5.00",
    currencyConversion: "1.00",
    socialSecurity: "2000.00",
    pensionIncome: "1000.00",
    withdrawalRate: "4.00",
    healthcareCosts: "10000.00",
    emergencyFund: "25000.00",
    debtAmount: "0.00",
    mortgageBalance: "0.00",
    riskTolerance: "moderate",
    investmentStyle: "balanced",
    marketVolatility: "12.00",
    maxDrawdown: "20.00",
    rebalancing: "yearly",
    expenseRatio: "0.50",
    requiredIncome: "60000.00",
    currentIncome: "75000.00",
    savingsRate: "20.00"
  });

  const [result, setResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);
  const chartRef = useRef();

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculateRetirement();
    }
  }, [inputs, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setInputs(prev => ({ 
      ...prev, 
      [name]: type === "checkbox" ? checked : value 
    }));
  };

  const calculateRetirement = () => {
    const {
      currentAge,
      retirementAge,
      lifeExpectancy,
      currentSavings,
      contribution,
      contributionFrequency,
      annualReturn,
      targetCorpus,
      inflationRate,
      annualExpenses,
      taxRate,
      stepUp,
      currencyConversion,
      socialSecurity,
      pensionIncome,
      withdrawalRate,
      healthcareCosts,
      emergencyFund,
      debtAmount,
      mortgageBalance,
      marketVolatility,
      expenseRatio
    } = inputs;

    const current = parseInt(currentAge) || 35;
    const retirement = parseInt(retirementAge) || 65;
    const life = parseInt(lifeExpectancy) || 85;
    const yearsToRetirement = Math.max(1, retirement - current);
    const retirementYears = Math.max(1, life - retirement);

    const currentSave = parseFloat(currentSavings) || 0;
    const contrib = parseFloat(contribution) || 0;
    const annualRet = (parseFloat(annualReturn) || 0) / 100;
    const inflation = (parseFloat(inflationRate) || 0) / 100;
    const tax = (parseFloat(taxRate) || 0) / 100;
    const step = (parseFloat(stepUp) || 0) / 100;
    const conversion = parseFloat(currencyConversion) || 1;
    const withdrawal = (parseFloat(withdrawalRate) || 0) / 100;
    const vol = (parseFloat(marketVolatility) || 0) / 100;
    const expense = (parseFloat(expenseRatio) || 0) / 100;

    // Adjust frequency
    const freqMultiplier = {
      monthly: 12,
      quarterly: 4,
      yearly: 1
    }[contributionFrequency] || 12;

    // Accumulation phase simulation
    const accumulationData = [];
    let portfolioValue = currentSave;
    let totalContributions = currentSave;
    let currentContrib = contrib * freqMultiplier;

    for (let year = 0; year <= yearsToRetirement; year++) {
      if (year > 0) {
        // Apply step-up to contributions
        currentContrib *= (1 + step);
        
        // Add annual contribution
        portfolioValue += currentContrib;
        totalContributions += currentContrib;

        // Apply investment growth with volatility
        const netReturn = annualRet - expense;
        portfolioValue *= (1 + netReturn);
      }

      accumulationData.push({
        year: current + year,
        age: current + year,
        portfolioValue: portfolioValue * conversion,
        contributions: totalContributions * conversion,
        annualContribution: currentContrib * conversion
      });
    }

    const retirementCorpus = portfolioValue * conversion;

    // Distribution phase simulation
    const distributionData = [];
    let retirementPortfolio = retirementCorpus;
    const annualExpense = parseFloat(annualExpenses) || 50000;
    const socialSec = parseFloat(socialSecurity) * 12 || 0;
    const pension = parseFloat(pensionIncome) * 12 || 0;
    const healthcare = parseFloat(healthcareCosts) || 0;
    const requiredIncome = parseFloat(inputs.requiredIncome) || annualExpense;

    let yearsToDepletion = retirementYears;
    let sustainable = true;

    for (let year = 0; year <= retirementYears; year++) {
      if (year > 0) {
        // Calculate annual withdrawal needs
        const inflatedExpenses = annualExpense * Math.pow(1 + inflation, year);
        const totalIncomeNeeded = inflatedExpenses + healthcare;
        const portfolioWithdrawal = Math.max(0, totalIncomeNeeded - socialSec - pension);
        
        // Safe withdrawal calculation
        const safeWithdrawal = retirementPortfolio * withdrawal;
        const actualWithdrawal = Math.min(portfolioWithdrawal, safeWithdrawal);

        // Apply withdrawal
        retirementPortfolio -= actualWithdrawal;

        // Apply investment growth during retirement
        const netReturn = (annualRet * 0.7) - expense; // Conservative returns in retirement
        retirementPortfolio *= (1 + netReturn);

        // Check if portfolio is depleted
        if (retirementPortfolio <= 0) {
          yearsToDepletion = year;
          sustainable = false;
          break;
        }
      }

      distributionData.push({
        year: retirement + year,
        age: retirement + year,
        portfolioValue: retirementPortfolio * conversion,
        annualIncome: (socialSec + pension + (year > 0 ? Math.min(
          annualExpense * Math.pow(1 + inflation, year),
          retirementPortfolio * withdrawal
        ) : 0)) * conversion
      });
    }

    // Calculate key metrics
    const target = parseFloat(targetCorpus) || 0;
    const goalAchievement = target > 0 ? (retirementCorpus / target) * 100 : null;
    const monthlyRetirementIncome = retirementCorpus * withdrawal / 12;
    const savingsRate = (parseFloat(inputs.savingsRate) || 0) / 100;
    const currentInc = parseFloat(inputs.currentIncome) || 75000;
    const requiredMonthlySavings = target > 0 ? 
      calculateRequiredSavings(target, yearsToRetirement, annualRet, currentSave) : 0;

    const readinessScore = calculateReadinessScore(
      retirementCorpus,
      target,
      yearsToRetirement,
      current,
      retirement
    );

    const resultData = {
      retirementCorpus,
      totalContributions: totalContributions * conversion,
      yearsToRetirement,
      retirementYears,
      monthlyRetirementIncome,
      goalAchievement,
      accumulationData,
      distributionData,
      sustainable,
      yearsToDepletion,
      readinessScore,
      requiredMonthlySavings,
      currentSavingsRate: (currentContrib / currentInc) * 100,
      inflationAdjustedCorpus: retirementCorpus / Math.pow(1 + inflation, yearsToRetirement)
    };

    setResult(resultData);
  };

  const calculateRequiredSavings = (target, years, returnRate, currentSavings) => {
    const futureValueOfCurrent = currentSavings * Math.pow(1 + returnRate, years);
    const remainingNeeded = target - futureValueOfCurrent;
    
    if (remainingNeeded <= 0) return 0;
    
    const monthlyRate = returnRate / 12;
    const months = years * 12;
    
    return remainingNeeded * monthlyRate / (Math.pow(1 + monthlyRate, months) - 1);
  };

  const calculateReadinessScore = (corpus, target, yearsToRetirement, currentAge, retirementAge) => {
    let score = 0;
    
    // Corpus achievement (40% weight)
    if (target > 0) {
      score += Math.min((corpus / target) * 40, 40);
    }
    
    // Time factor (30% weight)
    const yearsRatio = (retirementAge - currentAge) / 40; // Assuming 40-year career
    score += yearsRatio * 30;
    
    // Savings rate (20% weight)
    const savingsRate = parseFloat(inputs.savingsRate) || 15;
    score += Math.min((savingsRate / 20) * 20, 20);
    
    // Emergency fund (10% weight)
    const emergencyFund = parseFloat(inputs.emergencyFund) || 0;
    const monthlyExpenses = (parseFloat(inputs.annualExpenses) || 50000) / 12;
    const emergencyMonths = emergencyFund / monthlyExpenses;
    score += Math.min((emergencyMonths / 6) * 10, 10);
    
    return Math.min(score, 100);
  };

  const calculateSocialSecurity = () => {
    const { currentIncome, currentAge, retirementAge } = inputs;
    
    const income = parseFloat(currentIncome) || 75000;
    const current = parseInt(currentAge) || 35;
    const retirement = parseInt(retirementAge) || 65;
    const yearsWorked = retirement - current;
    
    // Simplified Social Security calculation
    const averageIndexedEarnings = income * 0.9; // Approximation
    const primaryInsuranceAmount = averageIndexedEarnings * 0.32; // Simplified formula
    
    return {
      estimatedBenefit: primaryInsuranceAmount / 12,
      yearsOfWork: yearsWorked,
      fullRetirementAge: 67
    };
  };

  const calculateWithdrawalStrategy = () => {
    const { retirementCorpus, withdrawalRate, inflationRate, lifeExpectancy, retirementAge } = result || {};
    
    if (!retirementCorpus) return null;
    
    const corpus = retirementCorpus;
    const rate = parseFloat(inputs.withdrawalRate) || 4;
    const inflation = parseFloat(inputs.inflationRate) || 3;
    const life = parseInt(inputs.lifeExpectancy) || 85;
    const retirement = parseInt(inputs.retirementAge) || 65;
    const years = life - retirement;
    
    const initialWithdrawal = corpus * (rate / 100);
    const safeWithdrawalYears = Math.log(2) / Math.log(1 + (rate - inflation) / 100);
    
    return {
      initialAnnualWithdrawal: initialWithdrawal,
      initialMonthlyWithdrawal: initialWithdrawal / 12,
      safeWithdrawalYears,
      portfolioLongevity: Math.min(years, safeWithdrawalYears),
      withdrawalStrategy: rate <= 4 ? "Conservative" : rate <= 5 ? "Moderate" : "Aggressive"
    };
  };

  const resetCalculator = () => {
    setInputs({
      currentAge: "35",
      retirementAge: "65",
      lifeExpectancy: "85",
      currentSavings: "50000.00",
      contribution: "1000.00",
      contributionFrequency: "monthly",
      annualReturn: "8.00",
      targetCorpus: "1000000.00",
      inflationRate: "3.00",
      annualExpenses: "50000.00",
      taxRate: "15.00",
      stepUp: "5.00",
      currencyConversion: "1.00",
      socialSecurity: "2000.00",
      pensionIncome: "1000.00",
      withdrawalRate: "4.00",
      healthcareCosts: "10000.00",
      emergencyFund: "25000.00",
      debtAmount: "0.00",
      mortgageBalance: "0.00",
      riskTolerance: "moderate",
      investmentStyle: "balanced",
      marketVolatility: "12.00",
      maxDrawdown: "20.00",
      rebalancing: "yearly",
      expenseRatio: "0.50",
      requiredIncome: "60000.00",
      currentIncome: "75000.00",
      savingsRate: "20.00"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Metric,Value",
      `Retirement Corpus,${result.retirementCorpus.toFixed(2)}`,
      `Total Contributions,${result.totalContributions.toFixed(2)}`,
      `Years to Retirement,${result.yearsToRetirement}`,
      `Monthly Retirement Income,${result.monthlyRetirementIncome.toFixed(2)}`,
      `Readiness Score,${result.readinessScore.toFixed(2)}%`,
      `Goal Achievement,${result.goalAchievement ? result.goalAchievement.toFixed(2) + '%' : 'N/A'}`,
      `Sustainable,${result.sustainable ? 'Yes' : 'No'}`,
      `Years to Depletion,${result.yearsToDepletion}`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'retirement-analysis.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      ...result
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Calculation saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setResult(calc);
    setShowSaved(false);
    addNotification("Calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { 
        annualReturn: "6.00", 
        withdrawalRate: "3.50",
        riskTolerance: "low",
        investmentStyle: "conservative"
      },
      moderate: { 
        annualReturn: "8.00", 
        withdrawalRate: "4.00",
        riskTolerance: "moderate",
        investmentStyle: "balanced"
      },
      aggressive: { 
        annualReturn: "10.00", 
        withdrawalRate: "4.50",
        riskTolerance: "high",
        investmentStyle: "aggressive"
      }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset} retirement strategy applied`, "info");
  };

  // Chart Data
  const accumulationChartData = result ? {
    labels: result.accumulationData.map(d => `Age ${d.age}`),
    datasets: [
      {
        label: 'Portfolio Value',
        data: result.accumulationData.map(d => d.portfolioValue),
        borderColor: "#19692C",
        backgroundColor: "rgba(25, 105, 44, 0.1)",
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "#19692C",
        pointBorderColor: "#FFFFFF",
        pointBorderWidth: 2
      },
      {
        label: 'Total Contributions',
        data: result.accumulationData.map(d => d.contributions),
        borderColor: "#FFD93D",
        backgroundColor: "rgba(255, 217, 61, 0.1)",
        fill: false,
        borderDash: [5, 5],
        tension: 0.4
      }
    ]
  } : null;

  const distributionChartData = result ? {
    labels: result.distributionData.map(d => `Age ${d.age}`),
    datasets: [
      {
        label: 'Retirement Portfolio',
        data: result.distributionData.map(d => d.portfolioValue),
        borderColor: "#2a9d8f",
        backgroundColor: "rgba(42, 157, 143, 0.1)",
        fill: true,
        tension: 0.4
      },
      {
        label: 'Annual Income',
        data: result.distributionData.map(d => d.annualIncome),
        borderColor: "#e76f51",
        backgroundColor: "rgba(231, 111, 81, 0.1)",
        fill: false,
        borderDash: [5, 5],
        tension: 0.4
      }
    ]
  } : null;

  const readinessChartData = result ? {
    labels: ["Corpus Achievement", "Time Factor", "Savings Rate", "Emergency Fund"],
    datasets: [{
      data: [
        Math.min((result.retirementCorpus / (parseFloat(inputs.targetCorpus) || result.retirementCorpus)) * 40, 40),
        ((parseInt(inputs.retirementAge) - parseInt(inputs.currentAge)) / 40) * 30,
        Math.min(((parseFloat(inputs.savingsRate) || 15) / 20) * 20, 20),
        Math.min(((parseFloat(inputs.emergencyFund) || 0) / ((parseFloat(inputs.annualExpenses) || 50000) / 12 * 6)) * 10, 10)
      ],
      backgroundColor: ["#19692C", "#2a9d8f", "#FFD93D", "#e76f51"],
      borderWidth: 2,
      borderColor: darkMode ? "#1A202C" : "#FFFFFF",
      hoverOffset: 15
    }],
  } : null;

  const socialSecurityData = calculateSocialSecurity();
  const withdrawalStrategy = calculateWithdrawalStrategy();

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Retirement Investment Calculator</h1>
        </div>
        <p>Comprehensive retirement planning with accumulation phase, distribution phase, and readiness analysis</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">Retirement Plan</div>
                    <div className="saved-details">
                      <span className={`profit ${calc.retirementCorpus >= 0 ? 'positive' : 'negative'}`}>
                        ${calc.retirementCorpus.toFixed(2)}
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Retirement Calc'}
              </button>
              <button 
                className={`tab ${activeTab === "income" ? "active" : ""}`}
                onClick={() => setActiveTab("income")}
              >
                <i className="fas fa-money-bill-wave"></i>
                {isMobile ? 'Income' : 'Income Planning'}
              </button>
              <button 
                className={`tab ${activeTab === "withdrawal" ? "active" : ""}`}
                onClick={() => setActiveTab("withdrawal")}
              >
                <i className="fas fa-hand-holding-usd"></i>
                {isMobile ? 'Withdrawal' : 'Withdrawal Strategy'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Retirement Strategy Presets */}
          <div className="presets-section">
            <h4>Retirement Strategy:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Conservative
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Aggressive
              </button>
            </div>
          </div>

          {/* Mode Selection */}
          <div className="mode-selection">
            <div className="mode-buttons">
              <button 
                className={`mode-btn ${mode === "accumulation" ? "active" : ""}`}
                onClick={() => setMode("accumulation")}
              >
                <i className="fas fa-piggy-bank"></i>
                Accumulation Phase
              </button>
              <button 
                className={`mode-btn ${mode === "distribution" ? "active" : ""}`}
                onClick={() => setMode("distribution")}
              >
                <i className="fas fa-umbrella-beach"></i>
                Distribution Phase
              </button>
            </div>
            <div className="mode-description">
              {mode === "accumulation" 
                ? "Wealth building phase - Focus on saving and investment growth"
                : "Retirement phase - Focus on sustainable withdrawals and income"
              }
            </div>
          </div>

          {/* Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-user"></i>
                    Personal Details
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-user"></i> Current Age</label>
                      <input 
                        type="number" 
                        name="currentAge" 
                        value={inputs.currentAge} 
                        onChange={handleChange}
                        placeholder="35"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-flag"></i> Retirement Age</label>
                      <input 
                        type="number" 
                        name="retirementAge" 
                        value={inputs.retirementAge} 
                        onChange={handleChange}
                        placeholder="65"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-heart"></i> Life Expectancy</label>
                      <input 
                        type="number" 
                        name="lifeExpectancy" 
                        value={inputs.lifeExpectancy} 
                        onChange={handleChange}
                        placeholder="85"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-piggy-bank"></i>
                    Savings & Investments
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Current Savings</label>
                      <input 
                        type="number" 
                        name="currentSavings" 
                        value={inputs.currentSavings} 
                        onChange={handleChange}
                        placeholder="50000.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-hand-holding-usd"></i> Regular Contribution</label>
                      <input 
                        type="number" 
                        name="contribution" 
                        value={inputs.contribution} 
                        onChange={handleChange}
                        placeholder="1000.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-calendar"></i> Contribution Frequency</label>
                      <select name="contributionFrequency" value={inputs.contributionFrequency} onChange={handleChange}>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Expected Annual Return (%)</label>
                      <input 
                        type="number" 
                        name="annualReturn" 
                        value={inputs.annualReturn} 
                        onChange={handleChange}
                        placeholder="8.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-rocket"></i> Step-Up Contribution (%)</label>
                      <input 
                        type="number" 
                        name="stepUp" 
                        value={inputs.stepUp} 
                        onChange={handleChange}
                        placeholder="5.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-bullseye"></i> Target Retirement Corpus</label>
                      <input 
                        type="number" 
                        name="targetCorpus" 
                        value={inputs.targetCorpus} 
                        onChange={handleChange}
                        placeholder="1000000.00"
                        step="0.01"
                      />
                    </div>
                  </div>

                  {/* Advanced Settings */}
                  <h3>
                    <i className="fas fa-sliders-h"></i>
                    Advanced Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-line"></i> Inflation Rate (%)</label>
                      <input 
                        type="number" 
                        name="inflationRate" 
                        value={inputs.inflationRate} 
                        onChange={handleChange}
                        placeholder="3.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-file-invoice-dollar"></i> Tax on Gains (%)</label>
                      <input 
                        type="number" 
                        name="taxRate" 
                        value={inputs.taxRate} 
                        onChange={handleChange}
                        placeholder="15.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Expense Ratio (%)</label>
                      <input 
                        type="number" 
                        name="expenseRatio" 
                        value={inputs.expenseRatio} 
                        onChange={handleChange}
                        placeholder="0.50"
                        step="0.01"
                      />
                    </div>
                     <div className="input-field">
                      <label><i className="fa-solid fa-shield"></i> Risk Tolerance</label>
                      <select name="riskTolerance" value={inputs.riskTolerance} onChange={handleChange}>
                        <option value="low">Low</option>
                        <option value="moderate">Moderate</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-bar"></i> Investment Style</label>
                      <select name="investmentStyle" value={inputs.investmentStyle} onChange={handleChange}>
                        <option value="conservative">Conservative</option>
                        <option value="balanced">Balanced</option>
                        <option value="aggressive">Aggressive</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrows-rotate"></i> Rebalancing Frequency</label>
                      <select name="rebalancing" value={inputs.rebalancing} onChange={handleChange}>
                        <option value="none">None</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Income Planning Tab */}
          {activeTab === "income" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-money-bill-wave"></i>
                  Income & Expenses Planning
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-dollar-sign"></i> Current Annual Income</label>
                    <input 
                      type="number" 
                      name="currentIncome" 
                      value={inputs.currentIncome} 
                      onChange={handleChange}
                      placeholder="75000.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-percentage"></i> Curr. Savings Rate</label>
                    <input 
                      type="number" 
                      name="savingsRate" 
                      value={inputs.savingsRate} 
                      onChange={handleChange}
                      placeholder="20.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-dollar-sign"></i> Retirement Income</label>
                    <input 
                      type="number" 
                      name="requiredIncome" 
                      value={inputs.requiredIncome} 
                      onChange={handleChange}
                      placeholder="60000.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-file-invoice-dollar"></i> Annual Expenses</label>
                    <input 
                      type="number" 
                      name="annualExpenses" 
                      value={inputs.annualExpenses} 
                      onChange={handleChange}
                      placeholder="50000.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-hand-holding-medical"></i> Healthcare Costs</label>
                    <input 
                      type="number" 
                      name="healthcareCosts" 
                      value={inputs.healthcareCosts} 
                      onChange={handleChange}
                      placeholder="10000.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-piggy-bank"></i> Emergency Fund</label>
                    <input 
                      type="number" 
                      name="emergencyFund" 
                      value={inputs.emergencyFund} 
                      onChange={handleChange}
                      placeholder="25000.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-shield"></i> Social Security (mo)</label>
                    <input 
                      type="number" 
                      name="socialSecurity" 
                      value={inputs.socialSecurity} 
                      onChange={handleChange}
                      placeholder="2000.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-landmark"></i> Pension Income (mo)</label>
                    <input 
                      type="number" 
                      name="pensionIncome" 
                      value={inputs.pensionIncome} 
                      onChange={handleChange}
                      placeholder="1000.00"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Withdrawal Strategy Tab */}
          {activeTab === "withdrawal" && (
            <div className="risk-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-hand-holding-usd"></i>
                  Withdrawal Strategy
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-percentage"></i> Withdrawal Rate (%)</label>
                    <input 
                      type="number" 
                      name="withdrawalRate" 
                      value={inputs.withdrawalRate} 
                      onChange={handleChange}
                      placeholder="4.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-chart-bar"></i> Market Volatility (%)</label>
                    <input 
                      type="number" 
                      name="marketVolatility" 
                      value={inputs.marketVolatility} 
                      onChange={handleChange}
                      placeholder="12.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-arrow-down"></i> Max Drawdown (%)</label>
                    <input 
                      type="number" 
                      name="maxDrawdown" 
                      value={inputs.maxDrawdown} 
                      onChange={handleChange}
                      placeholder="20.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-dollar-sign"></i> Debt Amount</label>
                    <input 
                      type="number" 
                      name="debtAmount" 
                      value={inputs.debtAmount} 
                      onChange={handleChange}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-home"></i> Mortgage Balance</label>
                    <input 
                      type="number" 
                      name="mortgageBalance" 
                      value={inputs.mortgageBalance} 
                      onChange={handleChange}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-arrows-repeat"></i> Currency Conversion</label>
                    <input 
                      type="number" 
                      name="currencyConversion" 
                      value={inputs.currencyConversion} 
                      onChange={handleChange}
                      placeholder="1.00"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className={`history-profit ${calc.retirementCorpus >= 0 ? 'positive' : 'negative'}`}>
                          <i className="fas fa-piggy-bank"></i>
                          ${calc.retirementCorpus.toFixed(2)}
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-percentage"></i> Readiness: {calc.readinessScore.toFixed(2)}%</span>
                        <span><i className="fas fa-calendar"></i> Years: {calc.yearsToRetirement}</span>
                        <span><i className="fas fa-bullseye"></i> Goal: {calc.goalAchievement ? calc.goalAchievement.toFixed(2) + '%' : 'N/A'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "income" || activeTab === "withdrawal") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-piggy-bank"></i>
                    Retirement Readiness Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className="result-value positive">
                          ${result.retirementCorpus.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-money-bill-wave"></i>
                          Retirement Corpus
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.readinessScore.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-chart-line"></i>
                          Readiness Score
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          ${result.monthlyRetirementIncome.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-hand-holding-usd"></i>
                          Monthly Income
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.yearsToRetirement}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-clock"></i>
                          Years to Retirement
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-piggy-bank"></i> Total Contributions:</span>
                        <span>${result.totalContributions.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-chart-line"></i> Investment Gains:</span>
                        <span>${(result.retirementCorpus - result.totalContributions).toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-inflation"></i> Inflation Adjusted Corpus:</span>
                        <span>${result.inflationAdjustedCorpus.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-calendar"></i> Retirement Years:</span>
                        <span>{result.retirementYears} years</span>
                      </div>
                      {result.goalAchievement && (
                        <div className="detail-row">
                          <span><i className="fas fa-bullseye"></i> Goal Achievement:</span>
                          <span>{result.goalAchievement.toFixed(2)}%</span>
                        </div>
                      )}
                    </div>

                    {/* Sustainability Analysis */}
                    <div className="risk-summary">
                      <h4>Retirement Sustainability</h4>
                      <div className="risk-cards">
                        <div className="risk-card">
                          <div className={`risk-value ${result.sustainable ? 'positive' : 'negative'}`}>
                            {result.sustainable ? 'Sustainable' : 'At Risk'}
                          </div>
                          <div className="risk-label">Portfolio Status</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value">
                            {result.yearsToDepletion}
                          </div>
                          <div className="risk-label">Years to Depletion</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value">
                            {socialSecurityData.estimatedBenefit.toFixed(2)}
                          </div>
                          <div className="risk-label">Social Security (Monthly)</div>
                        </div>
                      </div>
                    </div>

                    {/* Charts */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-line"></i>
                        Accumulation Phase Projection
                      </h3>
                      <Line 
                        data={accumulationChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: true,
                              position: 'top'
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                callback: function(value) {
                                  return '$' + value.toLocaleString();
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div><br/>

                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Readiness Score Breakdown
                      </h3>
                      <Doughnut 
                        data={readinessChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `${context.label}: ${context.parsed.toFixed(1)} points`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-calculator fa-3x"></i>
                    <p>Enter your retirement details to see analysis</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "income" && socialSecurityData && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-money-bill-wave"></i>
                  Income Planning Analysis
                </h3>
                <div className="income-analysis">
                  <div className="results-grid">
                    <div className="result-card highlight">
                      <div className="result-value">
                        ${(parseFloat(inputs.requiredIncome) || 0).toLocaleString()}
                      </div>
                      <div className="result-label">
                        Required Annual Income
                      </div>
                    </div>
                    <div className="result-card">
                      <div className="result-value">
                        {inputs.savingsRate}%
                      </div>
                      <div className="result-label">
                        Current Savings Rate
                      </div>
                    </div>
                    <div className="result-card">
                      <div className="result-value">
                        ${socialSecurityData.estimatedBenefit.toFixed(2)}
                      </div>
                      <div className="result-label">
                        Estimated Social Security
                      </div>
                    </div>
                    <div className="result-card">
                      <div className="result-value">
                        ${(parseFloat(inputs.pensionIncome) * 12 || 0).toLocaleString()}
                      </div>
                      <div className="result-label">
                        Annual Pension Income
                      </div>
                    </div>
                  </div>

                  <div className="position-breakdown">
                    <h4>Retirement Income Sources</h4>
                    <div className="breakdown-item">
                      <span>Portfolio Withdrawals:</span>
                      <span>${(result?.monthlyRetirementIncome * 12 || 0).toFixed(2)}</span>
                    </div>
                    <div className="breakdown-item">
                      <span>Social Security:</span>
                      <span>${(socialSecurityData.estimatedBenefit * 12).toFixed(2)}</span>
                    </div>
                    <div className="breakdown-item">
                      <span>Pension Income:</span>
                      <span>${(parseFloat(inputs.pensionIncome) * 12 || 0).toFixed(2)}</span>
                    </div>
                    <div className="breakdown-item">
                      <span>Total Annual Income:</span>
                      <span>${(
                        (result?.monthlyRetirementIncome * 12 || 0) +
                        (socialSecurityData.estimatedBenefit * 12) +
                        (parseFloat(inputs.pensionIncome) * 12 || 0)
                      ).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "withdrawal" && withdrawalStrategy && (
              <div className="risk-results-panel">
                <h3>
                  <i className="fas fa-hand-holding-usd"></i>
                  Withdrawal Strategy Analysis
                </h3><br/>
                <div className="withdrawal-analysis">
                  <div className="results-grid">
                    <div className="result-card highlight">
                      <div className="result-value">
                        ${withdrawalStrategy.initialAnnualWithdrawal.toFixed(2)}
                      </div>
                      <div className="result-label">
                        Initial Annual Withdrawal
                      </div>
                    </div>
                    <div className="result-card">
                      <div className="result-value">
                        ${withdrawalStrategy.initialMonthlyWithdrawal.toFixed(2)}
                      </div>
                      <div className="result-label">
                        Initial Monthly Withdrawal
                      </div>
                    </div>
                    <div className="result-card">
                      <div className="result-value">
                        {withdrawalStrategy.portfolioLongevity.toFixed(1)}
                      </div>
                      <div className="result-label">
                        Portfolio Longevity (Years)
                      </div>
                    </div>
                    <div className="result-card">
                      <div className="result-value">
                        {withdrawalStrategy.withdrawalStrategy}
                      </div>
                      <div className="result-label">
                        Withdrawal Strategy
                      </div>
                    </div>
                  </div>

                  <div className="position-breakdown">
                    <h4>Withdrawal Guidance</h4>
                    <div className="breakdown-item">
                      <span>Safe Withdrawal Rate:</span>
                      <span>{inputs.withdrawalRate}%</span>
                    </div>
                    <div className="breakdown-item">
                      <span>Portfolio Success Rate:</span>
                      <span>{withdrawalStrategy.portfolioLongevity > 30 ? 'High' : 'Moderate'}</span>
                    </div>
                    <div className="breakdown-item">
                      <span>Recommended Adjustment:</span>
                      <span>{parseFloat(inputs.withdrawalRate) > 4 ? 'Consider lowering to 4%' : 'Appropriate'}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default RetirementInvestmentCalculator;